import { ClinicalTaskEditFields, getClinicalTaskEditFields } from '@bariendo/lib/clinical-tasks';
import { Task } from '@medplum/fhirtypes';
import { ReactElement, useCallback, useMemo } from 'react';
import { useTaskEdit } from '@/hooks/useTaskEdit';
import { TaskForm } from './TaskForm';

export interface EditTaskFormProps {
  /** Snapshot of the task as the user saw it — the version guard tests against this. */
  task: Task;
  /**
   * List refresh, forwarded into the guarded patch so a version conflict
   * replaces the stale row (same contract as complete/delete).
   */
  onChange?: () => void | Promise<void>;
  /** Called with the updated Task after a successful save (failures already alerted by the hook). */
  onSave: (task: Task) => void;
  onCancel: () => void;
}

export function EditTaskForm({ task, onChange, onSave, onCancel }: EditTaskFormProps): ReactElement {
  const { editTask, isUpdating } = useTaskEdit(onChange);
  const initialValues = useMemo(() => getClinicalTaskEditFields(task), [task]);

  const handleSubmit = useCallback(
    async (fields: ClinicalTaskEditFields) => {
      const updated = await editTask(task, fields);
      if (updated) {
        onSave(updated);
      }
    },
    [editTask, task, onSave]
  );

  return (
    <TaskForm
      loading={isUpdating}
      submitLabel="Save edit"
      initialValues={initialValues}
      onCancel={onCancel}
      onSubmit={handleSubmit}
    />
  );
}
